import type { Company } from '../types/auth';

/**
 * Utility functions for company-aware navigation
 */

export const encodePathSegment = (segment: string): string => {
  try {
    return encodeURIComponent(decodeURIComponent(segment));
  } catch {
    return encodeURIComponent(segment);
  }
};

export const decodePathSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

/**
 * Extract company ID from a company-specific URL
 * @param pathname - The URL pathname to parse
 * @returns The company ID if found, null otherwise
 */
export const extractCompanyIdFromPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/company\/([^\/]+)/);
  return match ? decodePathSegment(match[1]) : null;
};

/**
 * Extract the page path from a company-specific URL
 * @param pathname - The URL pathname to parse
 * @returns The page path (e.g., 'dashboard', 'cameras') if found, null otherwise
 */
export const extractPageFromPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/company\/[^\/]+\/(.+)$/);
  return match ? decodePathSegment(match[1]) : null;
};

/**
 * Check if a path is a company-specific route
 * @param pathname - The URL pathname to check
 * @returns True if it's a company route, false otherwise
 */
export const isCompanyRoute = (pathname: string): boolean => {
  return pathname.startsWith('/company/');
};

/**
 * Build a company-specific URL for a given page
 * @param companyId - The company ID
 * @param page - The page path (e.g., 'dashboard', 'cameras')
 * @returns The complete URL path
 */
export const buildCompanyUrl = (companyId: string, page: string = 'dashboard'): string => {
  return `/company/${encodePathSegment(companyId)}/${page}`;
};

/**
 * Convert a legacy URL to a company-specific URL
 * @param legacyPath - The legacy path (e.g., '/dashboard', '/cameras')
 * @param companyId - The company ID to use
 * @returns The new company-specific URL
 */
export const convertLegacyUrl = (legacyPath: string, companyId: string): string => {
  // Remove leading slash if present
  const page = legacyPath.startsWith('/') ? legacyPath.substring(1) : legacyPath;
  return buildCompanyUrl(companyId, page || 'dashboard');
};

/**
 * Get the current company from URL or fallback to provided company
 * @param pathname - Current URL pathname
 * @param companies - Available companies
 * @param fallbackCompany - Fallback company if URL doesn't contain one
 * @returns The company that should be active
 */
export const getActiveCompany = (
  pathname: string, 
  companies: Company[], 
  fallbackCompany?: Company | null
): Company | null => {
  const companyIdFromUrl = extractCompanyIdFromPath(pathname);
  
  if (companyIdFromUrl) {
    const companyFromUrl = companies.find(c => c.company_id === companyIdFromUrl);
    if (companyFromUrl) {
      return companyFromUrl;
    }
  }
  
  return fallbackCompany || (companies.length > 0 ? companies[0] : null);
};

/**
 * Navigate to the same page but with a different company
 * @param currentPath - Current URL pathname
 * @param newCompanyId - The new company ID to navigate to
 * @returns The new URL to navigate to
 */
export const switchCompanyInUrl = (currentPath: string, newCompanyId: string): string => {
  const pagePath = extractPageFromPath(currentPath);
  return buildCompanyUrl(newCompanyId, pagePath || 'dashboard');
}; 